import { create } from "zustand";

// Client UI state only (Front-End.md §0). The table is the primary surface; the map
// is an OPTIONAL window that reads selection from here. Linked selection is bidirectional
// ONLY while the map is open.
export interface FeatureRef {
  layer_id: string;
  feature_id: string;
}

// Folder collapse state persists per-browser (client-only UI, Front-End.md §A10).
const COLLAPSE_KEY = "sm_collapsed_folders";
function loadCollapsed(): Set<string> {
  try {
    return new Set(
      JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? "[]") as string[],
    );
  } catch {
    return new Set();
  }
}
function saveCollapsed(s: Set<string>) {
  try {
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...s]));
  } catch {
    /* ignore quota/private-mode */
  }
}

// A completed work order asked Visualize to solo one result layer once the
// field's soil layers have loaded. `prefer` is the analyte order (pH, then OM).
export interface PendingLayerFocus {
  fieldId: string;
  sampleEventId: string;
  prefer: string[];
}

interface UiState {
  selectedFieldId: string | null;
  mapFieldIds: string[];
  mapOpen: boolean;
  pendingLayerFocus: PendingLayerFocus | null;
  // Work orders page (profile menu; also where "Save and order" lands) — an overlay
  // view like the map, not a route (the app deliberately has no router).
  workOrdersOpen: boolean;
  // BULK DATA DUMP: two profile-menu workflows for adding fields/boundaries, zones, soil samples
  // and EC scans in BULK — 'jd' pulls many John Deere fields at once, 'upload' takes a pile of
  // mixed spatial files.
  bulkDump: "jd" | "upload" | null;
  errorsMenu: boolean;
  systemDashboard: boolean;
  // "Add Field Data" -> the table grows a per-field data-sources COLUMN (not a drawer)
  dataColumnOpen: boolean;
  // shared row<->feature highlight (sample point id, zone number, etc.)
  highlighted: FeatureRef | null;
  // new-field creation modal
  newFieldModal: "draw" | "jd" | null;
  // folder tree UI (server tree lives in react-query; these are ephemeral view bits)
  collapsedFolders: Set<string>;
  dragOverFolderId: string | null; // '__root__' for the Unfiled/root drop target
  /* * a field row is mid-drag — the Unfiled drop target appears for the duration even when empty */
  draggingField: boolean;
  // field whose name the Actions menu asked to rename (triggers inline edit)
  renamingFieldId: string | null;
  selectField: (id: string | null) => void;
  openMap: (id: string) => void;
  openMapFields: (ids: string[]) => void; // open the map for a SET of fields (focus the first)
  focusMapField: (id: string) => void; // switch which selected field the map is focused on
  closeMap: () => void;
  setPendingLayerFocus: (focus: PendingLayerFocus) => void;
  clearPendingLayerFocus: () => void;
  openWorkOrders: () => void;
  openBulkDump: (mode: "jd" | "upload") => void;
  closeBulkDump: () => void;
  openErrorsMenu: () => void;
  closeErrorsMenu: () => void;
  openSystemDashboard: () => void;
  closeSystemDashboard: () => void;
  closeWorkOrders: () => void;
  toggleDataColumn: () => void;
  highlight: (ref: FeatureRef | null) => void;
  openNewField: (m: "draw" | "jd") => void;
  closeNewField: () => void;
  toggleFolder: (id: string) => void;
  setDragOverFolder: (id: string | null) => void;
  setDraggingField: (v: boolean) => void;
  startRenameField: (id: string | null) => void;
  clearFieldScope: () => void; // drop ALL field/view-scoped state (org switch / logout)
}

export const useUiStore = create<UiState>((set) => ({
  selectedFieldId: null,
  mapFieldIds: [],
  mapOpen: false,
  pendingLayerFocus: null,
  workOrdersOpen: false,
  bulkDump: null,
  errorsMenu: false,
  systemDashboard: false,
  dataColumnOpen: false,
  highlighted: null,
  newFieldModal: null,
  collapsedFolders: loadCollapsed(),
  dragOverFolderId: null,
  draggingField: false,
  renamingFieldId: null,
  selectField: (id) => set({ selectedFieldId: id }),
  openMap: (id) =>
    set({ selectedFieldId: id, mapFieldIds: [id], mapOpen: true }),
  openMapFields: (ids) =>
    set({ selectedFieldId: ids[0] ?? null, mapFieldIds: ids, mapOpen: true }),
  focusMapField: (id) => set({ selectedFieldId: id, highlighted: null }),
  closeMap: () =>
    set({ mapOpen: false, highlighted: null, pendingLayerFocus: null }),
  setPendingLayerFocus: (focus) => set({ pendingLayerFocus: focus }),
  clearPendingLayerFocus: () => set({ pendingLayerFocus: null }),
  openWorkOrders: () => set({ workOrdersOpen: true }),
  openBulkDump: (mode) => set({ bulkDump: mode }),
  closeBulkDump: () => set({ bulkDump: null }),
  openErrorsMenu: () => set({ errorsMenu: true }),
  closeErrorsMenu: () => set({ errorsMenu: false }),
  openSystemDashboard: () => set({ systemDashboard: true }),
  closeSystemDashboard: () => set({ systemDashboard: false }),
  closeWorkOrders: () => set({ workOrdersOpen: false }),
  toggleDataColumn: () => set((s) => ({ dataColumnOpen: !s.dataColumnOpen })),
  highlight: (ref) => set({ highlighted: ref }),
  openNewField: (m) => set({ newFieldModal: m }),
  closeNewField: () => set({ newFieldModal: null }),
  toggleFolder: (id) =>
    set((s) => {
      const next = new Set(s.collapsedFolders);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveCollapsed(next);
      return { collapsedFolders: next };
    }),
  setDragOverFolder: (id) => set({ dragOverFolderId: id }),
  setDraggingField: (v) => set({ draggingField: v }),
  startRenameField: (id) => set({ renamingFieldId: id }),
  clearFieldScope: () =>
    set({
      selectedFieldId: null,
      mapFieldIds: [],
      mapOpen: false,
      pendingLayerFocus: null,
      workOrdersOpen: false,
      bulkDump: null,
      dataColumnOpen: false,
      highlighted: null,
      renamingFieldId: null,
      newFieldModal: null,
    }),
}));

// MIME types for native HTML5 drag-and-drop of table rows / folders (Front-End.md §A10).
export const DND_FIELD = "application/x-sm-field";
export const DND_FOLDER = "application/x-sm-folder";
export const ROOT_DROP = "__root__"; // Unfiled / move-to-root drop target id
